'use strict'
// Identifiers below intentionally mirror the backend money fields (amount_decimal, markup_amount,
// remaining_decimal, relieved_amount_decimal); renaming would break the contract — so camelcase is disabled.
/* eslint-disable camelcase */
/**
 * CP0.3 money-path characterization — distribution math EXTRACTED VERBATIM from Line1.vue.
 *
 * Pure, framework-free, dependency-free: `require()`-able by the Node characterization harness AND
 * `import`-able by the Vue/webpack build (CommonJS interop). Line1.vue now delegates to these so there is
 * ONE implementation under test — the recorded baseline is a true regression oracle for every later phase.
 * Behaviour is intentionally identical to the original inline code (same expressions, same quirks).
 */

/**
 * amount_with_markups_decimal from markup_type / markup_amount / amount_decimal.
 * Verbatim from Line1.vue updateAmountWithMarkups(): markup_type may be a raw string ('AMT'|'PCT'|'') or a
 * {value} object; 'AMT' adds a flat amount, 'PCT' adds a percentage, anything else passes the amount through.
 */
function applyMarkup (line) {
  var mType = line.markup_type.value || line.markup_type
  var markup_amount = line.markup_amount
  var amount_decimal = line.amount_decimal
  var newAmt
  switch (mType) {
    case 'AMT':
      newAmt = amount_decimal && markup_amount
        ? (parseFloat(amount_decimal) + parseFloat(markup_amount))
        : parseFloat(amount_decimal)
      break
    case 'PCT':
      newAmt = amount_decimal && markup_amount
        ? (parseFloat(amount_decimal) + (parseFloat(amount_decimal) * (parseFloat(markup_amount) / 100)))
        : parseFloat(amount_decimal)
      break
    default:
      newAmt = amount_decimal
  }
  return newAmt
}

/**
 * PO line `remaining_decimal` (and the revert decision) when a PO line's amount changes.
 * Verbatim from Line1.vue handleBlurEvent() PO branch. On a relieved status (POSTED/CLOSED/VOIDED/DELETED)
 * with an original line, remaining = new amount − already-relieved amount; a negative result means the edit is
 * rejected (caller reverts to the original line values and warns). Otherwise remaining = the (new) amount.
 * Returns { revert, remaining_decimal, amount_decimal? } — a pure description of what the caller must apply.
 */
function computePoRemaining (params) {
  var trxStatus = params.trxStatus
  var line = params.line
  var originalLine = params.originalLine
  var relievedStatuses = ['POSTED', 'CLOSED', 'VOIDED', 'DELETED']
  if (relievedStatuses.indexOf(trxStatus) !== -1) {
    if (originalLine && originalLine.amount_decimal) {
      var remaining = Number(line.amount_decimal) - Number(originalLine.relieved_amount_decimal)
      if (remaining < 0) {
        return {
          revert: true,
          amount_decimal: originalLine.amount_decimal,
          remaining_decimal: originalLine.remaining_decimal
        }
      }
      return { revert: false, remaining_decimal: remaining }
    }
    return { revert: false, remaining_decimal: Number(line.amount_decimal) }
  }
  return { revert: false, remaining_decimal: Number(line.amount_decimal) }
}

/**
 * Selection-label normalization for autocomplete/select money fields (account/item pickers).
 * Verbatim from Line1.vue/Line.vue handleSelect(): a label like '1000 - Cash' collapses to the code
 * before the ' - ' separator ('1000'); a label with no separator passes through unchanged. Extracted so
 * the display value the field shows after a selection is a single implementation under characterization.
 */
function normalizeSelectionLabel (label) {
  return (typeof label === 'string' && label.includes(' - ')) ? label.split(' - ')[0] : label
}

module.exports = {
  applyMarkup: applyMarkup,
  computePoRemaining: computePoRemaining,
  normalizeSelectionLabel: normalizeSelectionLabel
}
